// approvalCreateDetails.js — pure view-model for the create-campaign detail block on an
// `agent.approval` card. NO React. Both ChatInteractionCard (chat) and ApprovalArtifact (board
// drawer) render from this so the two surfaces cannot drift.
//
// Item [B]: a `propose_create_campaign` card's payload.change is {kind:'create_campaign', objective,
// name?, cbo?, daily_budget_cents?, event_id?} (proposeCreateCampaign.ts changeFor). This maps that
// change (+ payload.why) into labelled display rows. It renders ONLY fields that exist at propose
// time — objective/name/budget/why — and NEVER fabricates audience/dates/ad-copy (those do not
// exist on the propose payload; showing them would violate the anti-fabrication epic).
//
// Item [C] (meta-full-funnel): a FULL-FUNNEL create additionally carries change.funnel =
// {message, headline?, description?, adset_name?, ad_name?, resolved:{...}} where `resolved` is the
// SERVER-BUILT propose-time snapshot (Agent::Meta::FunnelResolver): page_id/page_name, banner_url,
// link, start/end_time + schedule_label, targeting + targeting_summary, objective, optimization_goal,
// optimization_downgraded, instagram_excluded? + instagram_exclusion_reason?, existing_campaigns?.
// Funnel rows are sourced ONLY from that snapshot +
// the agent-authored copy — the anti-fabrication rule holds: a row renders ONLY when its field
// exists on the payload (missing sub-field → row omitted, never "undefined"). A card with NO funnel
// renders EXACTLY the three campaign-only rows (regression-locked).
//
// ONE SNAPSHOT, ONE STORY (QA bug 2): the Objective row and the Optimization row are BOTH derived
// from `change.funnel.resolved` whenever it exists, so they physically cannot disagree. Previously
// the Objective row echoed the agent's raw proposed word and the Optimization row was derived from
// `optimization_downgraded` alone, which produced live cards reading "Objective: reach /
// Optimization: Sales (pixel)". There is exactly ONE Optimization row, and it is authoritative.
//
// The money `summary` (before_value/after_value) is deliberately NOT read here — for a create it is
// hardcoded to $0 → $0 (proposeCreateCampaign.ts / money_action_card.rb), which is why the board
// drawer used to show a meaningless "Change: $0 → $0" line for creates.

// The CREATE-FAMILY gate BOTH surfaces call before they build any rows. It is deliberately a
// FAMILY, not one kind: an append (kind 'create_adset' — one more event's paused ad set + creative
// + ad inside a campaign that already exists) is built from the SAME propose-time snapshot and the
// SAME anti-fabrication rules as a campaign create.
//
// HOTFIX 3.31.5 — it used to answer `false` for 'create_adset', so the renderers skipped
// createDetailRows entirely and two real customers were shown an append card carrying a title,
// Approve/Decline and NOTHING else (the board drawer showed "Action: create_adset / Change:
// $0 → $0"). The row model below had handled the append payload correctly the whole time; it was
// never called. Widening the gate is the fix. The name is kept because both renderers and their
// source guards bind to it; what it means is "this card renders the create-family detail block".
const CREATE_FAMILY_KINDS = ["create_campaign", "create_adset"]

export function isCreateCampaign(change) {
  return Boolean(change) && CREATE_FAMILY_KINDS.includes(change.kind)
}

// An APPEND — kind 'create_adset'. It renders a DIFFERENT first block from a create: it names no
// campaign of its own (it is joining one), and its money story is the opposite of a create's.
export function isAppend(change) {
  return Boolean(change) && change.kind === "create_adset"
}

// Objective label. The raw value may be the Graph OUTCOME_* value OR the friendly alias the model
// passed (`reach`, `pixel`, `rsvp`, …). This table MIRRORS Meta::ObjectiveRouting::ROUTING so the
// card names the SAME objective the server routed to — a card that read "Objective: reach" was
// echoing the agent's raw word, not the OUTCOME_AWARENESS campaign that actually gets created.
//
// "Sales / conversions", NOT "Sales / pixel conversions": whether a pixel is actually in play is the
// Optimization row's job (it is the row that knows the resolved goal). Promising "pixel conversions"
// here is what let one real card claim pixel conversions on the Objective row while the very next
// row disclosed "no pixel connected".
export function objectiveLabel(objective) {
  const v = String(objective || "").trim().toUpperCase()
  if (["OUTCOME_SALES", "PIXEL", "SALES", "CONVERSIONS"].includes(v)) return "Sales / conversions"
  if (["OUTCOME_TRAFFIC", "TRAFFIC", "CLICKS"].includes(v)) return "Traffic"
  if (["OUTCOME_ENGAGEMENT", "RSVP", "ENGAGEMENT"].includes(v)) return "RSVP / engagement"
  if (["OUTCOME_AWARENESS", "AWARENESS", "REACH", "IMPRESSIONS"].includes(v)) return "Awareness / reach"
  return objective ? String(objective) : "Not specified"
}

// THE ONE authoritative optimization line, derived from the SERVER-RESOLVED snapshot
// (Agent::Meta::FunnelResolver): the goal Meta will actually be given, and how it is billed.
//
// It used to be derived from `optimization_downgraded` ALONE — a flag that is only ever true on the
// sales-without-pixel branch — so every non-downgraded funnel printed "Sales (pixel)" no matter what
// it had resolved to. Real staging cards read "Objective: reach / Optimization: Sales (pixel)" and
// "Objective: Traffic / Optimization: Sales (pixel)". The goal is the authority now; the downgrade
// flag only adds the disclosure suffix on the branch that actually downgraded.
//
// Every ad set this funnel builds is IMPRESSIONS-billed (Meta::FunnelCreate#create_adset!), which is
// what "(impressions)" states on the reach line — the buy a human needs to see for an awareness ask.
export function optimizationLabel(resolved) {
  const goal = str(resolved && resolved.optimization_goal)
  if (!goal) return null
  switch (goal.trim().toUpperCase()) {
    case "REACH":
      return "Reach (impressions)"
    case "OFFSITE_CONVERSIONS":
      return "Sales (pixel)"
    case "LINK_CLICKS":
      // The ONLY branch the downgrade disclosure belongs on: a sales ask that had no pixel to
      // optimize against. A traffic/engagement funnel resolves to LINK_CLICKS by design — saying
      // "no pixel connected" there would invent a downgrade that never happened.
      return resolved.optimization_downgraded === true ? "Link clicks — no pixel connected" : "Link clicks"
    default:
      return goal // truthful echo — never a label we cannot stand behind
  }
}

// THE PLACEMENTS row — rendered ONLY when the server actually restricted placements.
//
// A full funnel normally runs on all Meta placements and the card says nothing about it (there is
// nothing to disclose). When Agent::Meta::FunnelResolver#check_instagram_placement finds that
// Instagram delivery is not safe — the ad account is not authorized for the Instagram account
// linked to the Page, or Meta could not be asked — it resolves publisher_platforms ['facebook']
// into the approved targeting, and the human must SEE that before approving: they are agreeing to
// a Facebook-only buy, not the all-placements buy they asked for.
//
// The reason string comes from the server snapshot verbatim (anti-fabrication: the card never
// invents why Instagram was dropped), and a snapshot without `instagram_excluded` renders NO row —
// which is every card from a healthy Business Manager, and every card minted before this shipped.
// agent-meta-open S2: the server now resolves placements from the proposal itself, so there IS
// something to disclose on every funnel card — `placements_summary` is the plain sentence
// Agent::Meta::FunnelResolver#compose_placements_summary composed from the resolved targeting
// ("Facebook + Instagram — no Messenger, no Audience Network · Facebook: feed, facebook_reels").
// The Instagram-exclusion wording still wins when it applies, because it is the one restriction a
// human must not have to infer from a platform list. A pre-S2 snapshot has neither key and renders
// NO row, exactly as before.
export function placementsLabel(resolved) {
  if (!resolved) return null
  if (resolved.instagram_excluded === true) {
    const reason = str(resolved.instagram_exclusion_reason)
    return reason
      ? `Facebook only — Instagram excluded (${reason})`
      : "Facebook only — Instagram excluded"
  }
  return str(resolved.placements_summary)
}

// The button the ad carries. Rendered only when the proposal chose one — the LEARN_MORE default
// needs no row of its own (the Destination row already says where it goes).
export function ctaLabel(resolved) {
  const cta = str(resolved && resolved.cta)
  if (!cta || cta === "LEARN_MORE") return null
  return cta.replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase())
}

// The pixel a conversion buy is measured by. A human approving "Sales (pixel)" should be able to
// see WHICH pixel, because that is where the numbers they will judge this campaign on come from.
export function pixelLabel(resolved) {
  const pixel = resolved && resolved.promoted_object && str(resolved.promoted_object.pixel_id)
  if (!pixel) return null
  const event = resolved.promoted_object && str(resolved.promoted_object.custom_event_type)
  return event ? `${pixel} · ${event}` : pixel
}

// THE STATUS ROW — the single most load-bearing fact on this card, and the one it never stated.
//
// Every object this approval creates is created PAUSED, at every level: the campaign
// (MetaActions::ApplyCreate#create_params), the ad set and the ad (Meta::FunnelCreate#create_adset!
// / #create_ad!, explicit status PAUSED because Graph's default is ACTIVE). Nothing spends until a
// human goes and turns it on, which is a SECOND, separate decision.
//
// The card said none of that. A human reading "Objective / Name / Budget: $20/day · CBO" and an
// Approve button has every reason to believe they are approving $20/day of spend starting now —
// which is the single scariest thing an agent can be misunderstood about, and the reason people
// hesitate over the button at all. Stating it removes a fear that was never justified.
//
// STATIC, and deliberately so: it is not read off the payload because it is not a property of this
// proposal — it is a property of the create path itself, enforced server-side at every level and
// re-asserted by assert_paused! on the sandbox leg. A row sourced from the payload could go missing
// on a card whose snapshot lacked the key; this one cannot. It is therefore exempt from the
// anti-fabrication rule that governs every other row here: it states a server-enforced invariant,
// not an unverified fact about this particular funnel.
//
// THE ONE EXCEPTION, added with the activate-on-create override: a change carrying
// `activate_on_create: true` breaks the invariant on purpose (MetaActions::ApplyCreate POSTs ACTIVE
// at every level), so `statusRow` below swaps in ACTIVATE_ROW for exactly those cards. The default —
// including every card minted before the flag existed, and every card that omits it — is unchanged.
export const STATUS_ROW = { key: "Status", value: "Created paused — separate approval to go live" }

// THE OTHER STATUS, and the one that had better be impossible to miss.
//
// A create carrying `activate_on_create: true` skips the second decision entirely: every object is
// POSTed ACTIVE and the campaign starts spending the moment this button is clicked. The paused
// promise above is the reason a human can approve a create card quickly — so a card where that
// promise does NOT hold must say the opposite just as plainly, in the same slot, or the approval is
// uninformed. It is the ONE row that reads as a warning, deliberately.
export const ACTIVATE_ROW = {
  key: "Status",
  value: "⚠ Goes live immediately on approval — this campaign starts spending right away",
}

// Wire booleans arrive as real booleans from the agent service and as strings from a form-encoded
// round trip; both mean the same thing to the human reading the card.
function isTrue(value) {
  return value === true || value === "true"
}

// The bottom-line row for THIS card. Fresh object per call so a renderer mutating a row can never
// corrupt the shared constant for the next card (regression-locked in the view-model suite).
export function statusRow(change) {
  return isTrue(change && change.activate_on_create) ? { ...ACTIVATE_ROW } : { ...STATUS_ROW }
}

// Budget label. CBO + a numeric daily_budget_cents → "$20/day · CBO". ABO (cbo falsy) → the budget
// is set at the ad-set step, not here. No budget at all → "No budget set". NEVER "$NaN".
// A LIFETIME budget (Meta write parity Phase 1) is the other budget shape a create may carry —
// exactly one of the two ever rides a change (Agent::Meta::FunnelResolver#check_lifetime_budget
// refuses both), so it is read FIRST and the daily branches below are untouched when it is absent.
// It says "lifetime" out loud because "$500 · CBO" next to a daily card reading "$20/day · CBO" is
// the difference between a month of budget and a month of rent.
export function budgetLabel(change) {
  const lifetime = positiveCents(change && change.lifetime_budget_cents)
  const cents = change && change.daily_budget_cents
  const hasCents = typeof cents === "number" && Number.isFinite(cents) && cents > 0
  if (change && change.cbo && lifetime) return `$${usd(lifetime)} lifetime · CBO`
  if (change && change.cbo && hasCents) return `$${usd(cents)}/day · CBO`
  if (change && change.cbo && !hasCents) return "No budget set"
  if (lifetime) return `$${usd(lifetime)} lifetime · ad-set budget`
  return "Ad-set budget (set at the ad-set step)" // ABO / cbo omitted
}

// Whole dollars where the cents are zero ("$10"), two decimals otherwise ("$3.29"). Shared by the
// create and append budget rows so the two cards speak money the same way.
function usd(cents) {
  return (cents / 100).toFixed(2).replace(/\.00$/, "")
}

function positiveCents(value) {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null
}

// THE APPEND BUDGET ROW — the single most misunderstood fact on an append card, and the reason a
// human hesitates over Approve.
//
// On a CBO campaign the shared campaign budget is the ceiling and Meta spreads it across the ad
// sets eligible to deliver, so adding one adds NO new spend. Saying that plainly is the whole
// point. On an ABO campaign each ad set carries its own budget, so the append DOES add spend and
// the card must name the figure.
//
// Sourced ONLY from the server snapshot (Agent::Meta::FunnelResolver#snapshot_append_budget!) —
// never from the create-path `budgetLabel`, which on an append would read "Ad-set budget (set at
// the ad-set step)" on a CBO card: false, and false in the scariest direction. A snapshot that
// resolved no budget shape renders NO row rather than a guess.
export function appendBudgetLabel(change, resolved) {
  const campaign = (resolved && resolved.campaign) || {}
  const cbo = typeof campaign.cbo === "boolean" ? campaign.cbo : resolved && resolved.cbo
  if (cbo === true) {
    const cents = positiveCents(campaign.budget_cents)
    if (!cents) return "Shared campaign budget — no new spend"
    const basis = campaign.budget_basis === "campaign_lifetime" ? "lifetime" : "/day"
    return basis === "lifetime"
      ? `$${usd(cents)} lifetime campaign budget — no new spend`
      : `$${usd(cents)}/day shared campaign budget — no new spend`
  }
  if (cbo === false) {
    const cents = positiveCents(resolved && resolved.daily_budget_cents) || positiveCents(change && change.daily_budget_cents)
    return cents ? `$${usd(cents)}/day · new ad set budget` : null
  }
  return null
}

// Which end(s) of the flight window this proposal picked itself. Both flags are written by
// Agent::Meta::FunnelResolver ONLY on the override branches, so a default window renders no row.
export function scheduleSourceLabel(resolved) {
  const start = resolved && resolved.start_time_overridden === true
  const end = resolved && resolved.end_time_overridden === true
  if (start && end) return "Start and end set by this proposal"
  if (start) return "Start set by this proposal (end is the event date)"
  if (end) return "End set by this proposal (start is the default 14-day run-up)"
  return null
}

// The pixel event a sales funnel optimizes for, in words a venue owner uses. An unrecognised value
// is echoed verbatim rather than relabelled — the server already refuses anything outside the
// sanctioned set, so an echo here can only ever be a value we chose to stop translating.
export function conversionEventLabel(resolved) {
  const raw = str(resolved && resolved.optimization_event)
  if (!raw) return null
  switch (raw.trim().toUpperCase()) {
    case "PURCHASE":
      return "Purchase"
    case "CONTENT_VIEW":
      return "Content view"
    case "INITIATED_CHECKOUT":
      return "Checkout started"
    default:
      return raw
  }
}

// The Page the ad runs from. S2 lets a proposal NAME a page that is switched off in Vesta's own
// settings (an explicit pick beats our housekeeping flag) — when that happens the card has to say
// so, because the customer turned it off for a reason.
export function pageLabel(resolved) {
  const name = str(resolved && resolved.page_name)
  if (!name) return null
  return resolved.page_inactive === true ? `${name} (switched off in Vesta — named on this proposal)` : name
}

// WHY this ad is not running around the venue. Every value is written by
// Agent::Meta::FunnelResolver as `geo_fallback` when — and only when — the radius it would rather
// have used could not be used, so a healthy venue-radius card renders no row at all.
//
// "company_address" is the one that earns a ⚠: the ad is centred on the BUSINESS address, which for
// a touring act or an off-site show is a different town from the event. The Audience row already
// says so in words; this row is what makes a skimmer stop.
export function geoFallbackLabel(resolved) {
  switch (resolved && resolved.geo_fallback) {
    case "company_address":
      return "⚠ Centred on your business address — this event has no address of its own"
    case "digital_event":
      return "Online event — targeted United States-wide instead of a venue radius"
    case "us_wide":
      return "No usable venue location — targeted United States-wide"
    default:
      return null
  }
}

// The DESTINATION row. Normally the host is the useful fact ("tickets.example.com" — is this my
// ticketing provider?). But when the AGENT overrode the link (resolver sets `link_overridden`), the
// host alone hides the entire decision: an override to a presale path, a partner's tracking
// wrapper, or the wrong show on the right domain all render as the same three words. An overridden
// link is a term of the approval, so the card shows the WHOLE URL (clamped, because a wrapped
// affiliate URL can run to hundreds of characters and would otherwise blow up the row).
export function destinationLabel(resolved) {
  const link = str(resolved && resolved.link)
  if (!link) return null
  return resolved.link_overridden === true ? clamp(link, 120) : linkHost(link)
}

// The click tagging, and WHOSE it is. "company" means Vesta applied the workspace's own Link
// Tracking setting (Company#custom_utm_params) because the proposal named none — the human should
// know the tags came from their settings page and not from the agent's imagination.
export function trackingLabel(resolved) {
  const tags = str(resolved && resolved.url_tags)
  if (!tags) return null
  return resolved.url_tags_source === "company"
    ? `UTMs: ${tags} (your Link Tracking setting)`
    : `UTMs: ${tags}`
}

// Non-empty-string guard: funnel rows render ONLY real snapshot values (anti-fabrication).
function str(value) {
  return typeof value === "string" && value.trim() !== "" ? value : null
}

// Clamp the agent-authored primary text for the card row (~140 chars, ellipsized).
function clamp(value, max = 140) {
  const s = str(value)
  if (!s) return null
  return s.length <= max ? s : `${s.slice(0, max - 1).trimEnd()}…`
}

// Hostname of the resolved destination link ("tickets.example.com"), null when unparseable.
function linkHost(value) {
  const s = str(value)
  if (!s) return null
  try {
    return new URL(s).host || null
  } catch (e) {
    return null
  }
}

// Item [C] — the funnel rows, sourced ONLY from change.funnel (agent copy) + change.funnel.resolved
// (the server snapshot the human is approving). Missing sub-field → row omitted, never "undefined".
function funnelRows(funnel) {
  const resolved = (funnel && funnel.resolved) || {}
  const rows = []
  const push = (key, value) => {
    if (value) rows.push({ key, value })
  }
  push("Audience", str(resolved.targeting_summary))
  // Immediately under Audience, for the same reason: it qualifies the line above it. Only present
  // when the resolver could NOT centre on the event's own location.
  push("Location note", geoFallbackLabel(resolved))
  // Immediately under Audience: both rows describe WHO/WHERE this ad reaches, and a placement
  // restriction is meaningless to a reader who has not just read the audience line.
  push("Placements", placementsLabel(resolved))
  push("Schedule", str(resolved.schedule_label))
  // Only when the proposal ASKED for a window (Agent::Meta::FunnelResolver writes these keys solely
  // on the override branches). The Schedule row above already shows the resolved dates; this row
  // says WHOSE dates they are, because "Jun 20 → Jul 1" reads as a Vesta default until told
  // otherwise, and a hand-picked window is a term of the approval.
  push("Schedule source", scheduleSourceLabel(resolved))
  push("Ad copy", clamp(funnel.message))
  push("Headline", str(funnel.headline))
  push("Image", str(resolved.banner_url) ? "Event banner ✓" : null)
  push("Page", pageLabel(resolved))
  push("Destination", destinationLabel(resolved))
  // agent-meta-open S2 — the button and the click tagging are terms of the approval now: a human
  // who approved "BUY TICKETS → tickets.example.com, tagged utm_source=vesta" must be able to see
  // both. Absent keys render no rows (anti-fabrication), so a pre-S2 card is unchanged.
  push("Call to action", ctaLabel(resolved))
  push("Tracking", trackingLabel(resolved))
  // Exactly ONE Optimization row, and only when the snapshot actually resolved a goal.
  push("Optimization", optimizationLabel(resolved))
  push("Pixel", pixelLabel(resolved))
  // The pixel the proposal NAMED that this goal cannot use (FunnelResolver#note_unused_requested_pixel).
  // Not a refusal — the buy is legal — but the card must not let "pixel_id: 123" read as "optimizing
  // against pixel 123" when no promoted_object was emitted.
  push("Pixel note", str(resolved.pixel_note) ? `⚠ ${resolved.pixel_note}` : null)
  // The conversion event the sales ad set optimizes against, and ONLY when the proposal chose one
  // (the resolver writes the key only on the override branch, so a default PURCHASE funnel renders
  // no row — its story is already told by "Sales (pixel)" above).
  push("Conversion event", conversionEventLabel(resolved))
  // ADVISORY only (Agent::Meta::FunnelResolver#thin_spread_note): a CBO campaign whose shared daily
  // budget is thin across the ad sets it feeds still appends — Meta has no per-ad-set floor — but the
  // human should see the per-show average before approving. Absent key → no row, as before.
  push("Budget note", str(resolved.budget_note) ? `⚠ ${resolved.budget_note}` : null)
  // The SHARPER duplicate warning, and append-only: this event already has an ad set inside THIS
  // campaign (Agent::Meta::FunnelResolver#duplicate_ad_set_names_in_campaign). "3 existing
  // campaigns for this event" does not tell a human they are about to add the same show twice to
  // the campaign in front of them. Named, because the names are what they will go look for.
  const dupes = Array.isArray(resolved.existing_ad_sets_for_event) ? resolved.existing_ad_sets_for_event.filter(str) : []
  if (dupes.length > 0) {
    push("Warning", `⚠ Already in this campaign as: ${dupes.join(", ")}`)
  }
  const existing = Array.isArray(resolved.existing_campaigns) ? resolved.existing_campaigns.length : 0
  if (existing > 0) {
    push("Warning", `⚠ ${existing} existing campaign${existing === 1 ? "" : "s"} for this event`)
  }
  return rows
}

// The APPEND head block — the three facts a create card answers with Objective/Name/Budget, asked
// the way an append has to ask them: WHICH EVENT, WHAT the new ad set is called, WHICH campaign it
// joins, and WHAT it costs. Every value comes from the server snapshot; a missing one omits its
// row (the anti-fabrication rule), because "Auto-named (Vesta + event)" on a card whose ad set the
// agent DID name, or a create-shaped budget line on a CBO append, is worse than silence.
function appendHeadRows(change, funnel) {
  const resolved = (funnel && funnel.resolved) || {}
  const rows = []
  const push = (key, value) => {
    if (value) rows.push({ key, value })
  }
  push("Event", str(resolved.event_name))
  push("Ad set", str(funnel && funnel.adset_name))
  push("Adding to", str(resolved.campaign && resolved.campaign.name))
  push("Objective", optionalObjectiveLabel(resolved))
  push("Budget", appendBudgetLabel(change, resolved))
  return rows
}

// Objective for an append: the EXISTING campaign's objective, as routed and snapshotted by the
// server (FunnelResolver#objective_source returns the campaign's own objective on the append
// branch). Unlike a create — where the agent proposed one and there is always something to name —
// an append with no snapshot has no honest objective to state, so the row is omitted rather than
// defaulted to "Not specified".
function optionalObjectiveLabel(resolved) {
  const objective = str(resolved && resolved.objective)
  return objective ? objectiveLabel(objective) : null
}

// The objective the card must NAME. The server-resolved snapshot wins whenever it exists: it is the
// routed Graph objective the campaign is actually created with (Meta::ObjectiveRouting), and it is
// the same snapshot the Optimization row reads — so the two rows are sourced from ONE fact and
// cannot contradict each other. A card with no funnel (or a pre-Item-C card) falls back to the raw
// objective the agent proposed, which is all that exists on those payloads.
function resolvedObjective(change) {
  const resolved = (change && change.funnel && change.funnel.resolved) || {}
  return str(resolved.objective) || change.objective
}

// The full ordered row list a renderer maps over. `name` omitted → a muted "auto-named" note row
// (event-name resolve is a deferred follow-up — see spec-B §3.5). A funnel create (Item C) appends
// the snapshot-sourced funnel rows; a plain create keeps EXACTLY the three campaign-only rows.
export function createDetailRows(payload) {
  const change = (payload && payload.change) || {}
  const funnel = change.funnel
  // An APPEND swaps ONLY the head block (see appendHeadRows). Everything below it — audience,
  // placements, schedule, copy, image, page, destination, optimization, budget note, warnings,
  // status — is the SAME snapshot-sourced list a create renders, from the SAME helper, so the two
  // cards cannot drift and an append can never grow a row a create does not have to justify.
  const rows = isAppend(change)
    ? appendHeadRows(change, funnel)
    : [
        { key: "Objective", value: objectiveLabel(resolvedObjective(change)) },
        { key: "Name", value: change.name && String(change.name).trim() !== "" ? String(change.name) : "Auto-named (Vesta + event)" },
        { key: "Budget", value: budgetLabel(change) },
      ]
  if (funnel && typeof funnel === "object") {
    rows.push(...funnelRows(funnel))
  }
  // Last, and exactly once: the bottom line a human should leave this card holding. It rides on
  // EVERY create-family card — plain campaign create, full funnel, and the append card (kind
  // 'create_adset', whose ad set and ad are created just as paused) — because the fact is the same
  // on all of them, EXCEPT on a card that asked to go live on approval (see ACTIVATE_ROW), where
  // the opposite fact is the one the human has to see.
  rows.push(statusRow(change))
  return rows
}

// Why block: summary line + optional detail bullets. Returns null when there's nothing to show.
export function whyBlock(payload) {
  const why = (payload && payload.why) || {}
  const summary = why.summary && String(why.summary).trim() !== "" ? String(why.summary) : null
  const details = Array.isArray(why.details) ? why.details.filter((d) => d && String(d).trim() !== "") : []
  if (!summary && details.length === 0) return null
  return { summary, details }
}

// 2026-10-21 — a ListSend approval card (change.kind 'listsend_call', channel 'listsend'). It has no
// money line (its summary before/after is $0 → $0), so both surfaces render the SERVER-BUILT
// disclosure rows instead (Agent::MoneyActionCard#listsend_disclosure): what will happen, the exact
// ListSend action, and its arguments. Rows come only from the payload — nothing is composed here.
export function isListsendCall(change) {
  return Boolean(change) && change.kind === "listsend_call"
}

export function listsendDetailRows(payload) {
  const blocks = Array.isArray(payload && payload.disclosure) ? payload.disclosure : []
  return blocks
    .filter((b) => b && b.heading && b.body != null && String(b.body).trim() !== "")
    .map((b) => ({ key: String(b.heading), value: String(b.body) }))
}

